import type { FileUpdateMessageDatagramBuf } from "~/classes/generated/message"
import type { FileUpdateResponseDatagramBuf } from "~/classes/generated/response"

import { StaticFile } from "~/classes/file/StaticFile"
import { DatagramBuf } from "~/classes/generated/datagram"
import { FileUpdateRequestDatagramBuf, RequestDatagramBuf } from "~/classes/generated/request"
import { DatagramBufferBuilder } from "~/utils/flatbuffers/builder"
import { addRequestIdToBuf, parseDatagramError, parseMessageId, parseRequestId, parseSenderId } from "~/utils/flatbuffers/datagram"
import { addFileToBuf, parseFile } from "~/utils/flatbuffers/file"
import { parseFileId } from "~/utils/flatbuffers/id"
import { maybe } from "~/utils/helpers"

const console = useLogger("file/update")

export function parseFileUpdateRequestDatagramBuf(
  datagram: FileUpdateRequestDatagramBuf,
): FileUpdateRequestDatagram {
  return {
    requestId: parseRequestId(datagram.requestId()!),
    file: parseFile(datagram.file()!),
    offline: datagram.offline(),
  }
}

export function sendFileUpdateRequests(requests: FileUpdateRequestDatagram[]) {
  const { sendWorkerDatagramBuf } = useCache()

  const fbb = new DatagramBufferBuilder(64)

  const requestVector = []
  for (const request of requests) {
    const file = addFileToBuf(fbb, request.file)
    const requestId = addRequestIdToBuf(fbb, request.requestId)
    FileUpdateRequestDatagramBuf.startFileUpdateRequestDatagramBuf(fbb)
    FileUpdateRequestDatagramBuf.addRequestId(fbb, requestId)
    FileUpdateRequestDatagramBuf.addFile(fbb, file)
    FileUpdateRequestDatagramBuf.addOffline(fbb, request.offline)
    const updateRequest = FileUpdateRequestDatagramBuf.endFileUpdateRequestDatagramBuf(fbb)

    RequestDatagramBuf.startRequestDatagramBuf(fbb)
    RequestDatagramBuf.addFileUpdateRequest(fbb, updateRequest)
    requestVector.push(RequestDatagramBuf.endRequestDatagramBuf(fbb))
  }

  const vector = DatagramBuf.createRequestVector(fbb, requestVector)
  DatagramBuf.startDatagramBuf(fbb)
  DatagramBuf.addRequest(fbb, vector)
  fbb.finish(DatagramBuf.endDatagramBuf(fbb))
  sendWorkerDatagramBuf(fbb.asUint8Array())
}

export function parseFileUpdateResponseDatagramBuf(
  datagram: FileUpdateResponseDatagramBuf,
): FileUpdateResponseDatagram {
  return {
    requestId: parseRequestId(datagram.requestId()!),
    error: maybe(parseDatagramError)(datagram.error()),
    file: maybe(parseFile)(datagram.file()),
    fileId: parseFileId(datagram.fileId()!),
    offline: datagram.offline(),
  }
}

export function handleFileUpdateResponseDatagramBuf(datagram: FileUpdateResponseDatagramBuf) {
  onFileUpdateResponse(parseFileUpdateResponseDatagramBuf(datagram))
}

export function handleFileUpdateMessageDatagramBuf(datagram: FileUpdateMessageDatagramBuf) {
  onFileUpdateMessage({
    messageId: parseMessageId(datagram.messageId()!),
    senderId: parseSenderId(datagram.senderId()!),
    file: parseFile(datagram.file()!),
  })
}

export function onFileUpdateResponse(response: FileUpdateResponseDatagram, quiet = false) {
  const { emitEntityEvent, emitInterfaceEvent } = useEvents()

  emitInterfaceEvent("fileUpdateResponse", response, response.requestId)

  if (response.error)
    return onFileUpdateErrored(response.error, quiet)
  if (!response.file)
    throw new Error("No file in update response")

  const iFile = new StaticFile(response.file)
  emitEntityEvent("fileProperties", iFile.id, iFile.inner(), response.offline)

  const parentId = iFile.inferredParentId()
  if (!parentId) {
    // console.warn("No parent id in file update response, should at least have a path?", response)
    return
  }
  emitEntityEvent("childProperties", parentId, iFile.inner(), response.offline)
}

export function onFileUpdateMessage(response: FileUpdateMessageDatagram) {
  const { emitEntityEvent } = useEvents()

  const iFile = new StaticFile(response.file)
  emitEntityEvent("fileProperties", iFile.id, iFile.inner(), false)

  if (iFile.isRootFolder())
    return

  const parentId = iFile.inferredParentId()
  if (!parentId) {
    console.error("No parent id in file update response", response)
    throw new Error("no parent id in file update response")
  }
  emitEntityEvent("childProperties", parentId, iFile.inner(), false)
}

export function onFileUpdateErrored(
  error: DatagramError,
  quiet = false,
) {
  const { notifyDatagramError } = useAlerts()

  console.error(error)

  if (!quiet)
    notifyDatagramError(error, "File update was unsuccessful")
}
